import React, { useState } from 'react';
import {
  ButtonGroup,
  Table,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import ListTableRow from 'components/Table/ListTableRow';
import { ChevronDown, Eye } from 'react-feather';
import { formatHHmmDDMMYYYY } from 'helpers/format-time';
import { showDurationTime, stripHtml } from 'helpers';
import adaptiveApi from 'api/adaptiveAiApi';
import { UNIT_STATUS, UNIT_STATUS_TOOL_TIP } from 'constants/constants';
import AdaptiveAttemptQuestionDetail, {
  AdaptiveAnswerReadOnly,
  renderQuestionStem,
} from './AdaptiveAttemptQuestionDetail';
import { RiTimerFill } from 'react-icons/ri';
import { MdExtension, MdOutlineTimer, MdReviews } from 'react-icons/md';
import { ThunderboltOutlined } from '@ant-design/icons';
import {
  PageShell,
  Container,
  SummaryCard,
  SummaryHeader,
  SummaryTitle,
  SummarySubtitle,
  ScoreGradient,
  StatsRow,
  StatTile,
  StatIconBadge,
  StatContent,
  StatValue,
  StatLabel,
  TableCard,
  EyeActionButton,
  SectionBlock,
  StyledCollapse,
  BorderlessPanel,
  MicroHeaderRow,
  MicroIconBadge,
  MicroTitle,
  AiPill,
  MicroBody,
  BreakdownHeading,
  QuestionCollapse,
  QuestionPanel,
  QHeaderRow,
  QNumberLabel,
  QPreviewText,
  WeightPill,
  WeightPillLabel,
  QuestionBody,
  QuestionAnswerSection,
  EmptyStateCard,
  HistoryStatusCell,
  CursorPointer,
  ScrollableModalBody,
  QuestionStatusBadge,
  getWeightTone,
} from './AdaptiveStyles';

function statusColorForAttempt(status) {
  const map = {
    FAILED: '#cb6363',
    COMPLETED: '#52c41a',
    PASSED: '#52c41a',
    PENDING: '#faad14',
    NOT_ATTEMPT: '#8c8c8c',
  };
  return map[status] || '#646871';
}

function getQuestionWeightLabel(item) {
  const w = item?.weight;
  const uw = item?.userWeight;
  if (w == null && uw == null) return null;
  const earned =
    item?.status === UNIT_STATUS.FAILED
      ? 0
      : item?.status === UNIT_STATUS.COMPLETED
      ? w
      : uw ?? 0;
  return `${earned}/${w ?? '—'}`;
}

const TrackingResultAdaptive = ({ isBusy, unitSelected }) => {
  const [resultTracking, setResultTracking] = useState(null);
  const isCompleted = unitSelected?.status === UNIT_STATUS.COMPLETED;
  const unitId = unitSelected?._id;
  const totalWeight = unitSelected?.totalWeight ?? 5;
  const userWeight = unitSelected?.totalUserWeight ?? 0;

  const fetchTracking = async () => {
    try {
      const res = await adaptiveApi.getTableApi(unitId);
      setResultTracking(res);
    } catch (error) {}
  };

  React.useEffect(() => {
    if (unitId) {
      fetchTracking();
    }
  }, [unitId]);

  const [selectedHistory, setSelectedHistory] = useState({
    history: false,
    lastUpdate: false,
  });

  const [questionDetailModal, setQuestionDetailModal] = useState(null);

  const closeHistoryModal = () => {
    setSelectedHistory((prev) => ({ ...prev, history: false }));
    setQuestionDetailModal(null);
  };

  const thead = [
    {
      param: '',
      name: ' Users',
      style: { width: 200, textAlign: 'left' },
    },
    {
      param: '',
      name: 'Date & Time of submission',
      style: { width: 350, textAlign: 'center' },
    },
    {
      param: '',
      name: 'Actions',
      style: {
        width: 200,
        textAlign: 'center',
      },
    },
  ];

  const rows = Array.isArray(resultTracking)
    ? resultTracking
    : resultTracking?.data || [];

  const arrayConstant = [
    {
      id: 1,
      icon: <MdOutlineTimer size={18} />,
      value: <>{unitSelected?.points ? `${unitSelected?.points}%` : '--'}</>,
      label: 'Score',
    },
    {
      id: 2,
      icon: <RiTimerFill size={18} />,
      value: (
        <>
          {unitSelected?.duration
            ? showDurationTime(unitSelected?.duration)
            : '--'}
        </>
      ),
      label: 'Time Taken',
    },
    {
      id: 3,
      icon: <MdExtension size={18} />,
      value: (
        <>
          {unitSelected?.adaptiveAttemptData?.attempt
            ? unitSelected?.adaptiveAttemptData?.attempt
            : 1}
        </>
      ),
      label: 'No. Of Attempts',
    },
    {
      id: 4,
      icon: <MdOutlineTimer size={18} />,
      value: (
        <>
          {userWeight ?? '--'}/{totalWeight ?? '--'}
        </>
      ),
      label: 'Weightage',
    },
    {
      id: 5,
      icon: <MdReviews size={18} />,
      value: (
        <>
          {unitSelected?.status === UNIT_STATUS.COMPLETED
            ? UNIT_STATUS_TOOL_TIP.COMPLETED
            : UNIT_STATUS_TOOL_TIP.INCOMPLETED}
        </>
      ),
      label: 'Status',
    },
  ];

  const scoreDisplay =
    unitSelected?.points != null ? `${unitSelected.points}%` : '--';

  return (
    <PageShell className="overflow-x-auto mt-1">
      <Container>
        <SummaryCard>
          <SummaryHeader>
            <div>
              <SummaryTitle>Completed</SummaryTitle>
              <SummarySubtitle>
                {isCompleted && unitSelected?.submittedDate
                  ? formatHHmmDDMMYYYY(unitSelected?.submittedDate)
                  : '—'}
              </SummarySubtitle>
            </div>
            <ScoreGradient>{scoreDisplay}</ScoreGradient>
          </SummaryHeader>

          <StatsRow>
            {arrayConstant.map((Item) => (
              <StatTile key={Item.id}>
                <StatIconBadge>{Item.icon}</StatIconBadge>
                <StatContent>
                  <StatValue>{Item.value}</StatValue>
                  <StatLabel>{Item.label}</StatLabel>
                </StatContent>
              </StatTile>
            ))}
          </StatsRow>
        </SummaryCard>

        <TableCard>
          <Table className="table-header-custom mb-0" responsive>
            <thead>
              <tr>
                {thead.map((item) => (
                  <th
                    key={item.name}
                    style={item.style || {}}
                    className={'cursor-pointer'}
                  >
                    {item.name}
                    {item.param && (
                      <span>
                        <ChevronDown size={16} />
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <ListTableRow isBusy={isBusy} col={3} length={rows.length}>
                {rows.map((item, index) => {
                  const user = item?.user || {};
                  const attempts = item?.attempts || [];
                  const latestAttempt = attempts[attempts.length - 1];
                  const submissionTime =
                    latestAttempt?.questions?.[0]?.updatedAt;

                  return (
                    <tr key={index}>
                      <td className="text-left bold">
                        {user?.fullName ||
                          `${user?.firstName || ''} ${user?.lastName || ''}`}
                      </td>
                      <td className="text-center">
                        {submissionTime
                          ? formatHHmmDDMMYYYY(submissionTime)
                          : '--'}
                      </td>
                      <td className="text-center pl-0">
                        <ButtonGroup className="mb-1 ">
                          <EyeActionButton
                            id={`id-view-${index}`}
                            onClick={() =>
                              setSelectedHistory((preVal) => ({
                                ...preVal,
                                history: item,
                              }))
                            }
                          >
                            <Eye size={15} />
                            <UncontrolledTooltip
                              placement="top"
                              className="d-md-block d-none"
                              target={`id-view-${index}`}
                            >
                              History
                            </UncontrolledTooltip>
                          </EyeActionButton>
                        </ButtonGroup>
                      </td>
                    </tr>
                  );
                })}
              </ListTableRow>
            </tbody>
          </Table>
        </TableCard>

        {selectedHistory.history && (
          <Modal
            isOpen={!!selectedHistory.history}
            toggle={closeHistoryModal}
            size="lg"
            centered={true}
          >
            <ModalHeader toggle={closeHistoryModal}>History</ModalHeader>
            <ModalBody>
              <div className="overflow-x-auto">
                <Table className="table-header-custom" responsive>
                  <thead>
                    <tr>
                      <th>Date & Time of submission</th>
                      <th className="text-center">View detail</th>
                      <th>Users</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedHistory.history.attempts?.map((attempt, idx) => {
                      const submittedAt =
                        attempt?.questions?.[0]?.updatedAt ??
                        attempt?.updatedAt;

                      let statusText =
                        UNIT_STATUS_TOOL_TIP[attempt.status] ?? attempt.status;
                      let statusColor = statusColorForAttempt(attempt.status);

                      if (
                        statusText === 'Not Passed' ||
                        statusText === 'NOT PASSED' ||
                        attempt.status === 'FAILED' ||
                        attempt.status === 'NOT_PASSED'
                      ) {
                        statusText = 'Incomplete';
                        statusColor = '#ef4444';
                      } else if (
                        statusText === 'Completed' ||
                        statusText === 'COMPLETED' ||
                        attempt.status === 'COMPLETED'
                      ) {
                        statusText = 'Completed';
                        statusColor = '#10b981';
                      }

                      return (
                        <tr key={attempt._id ?? idx}>
                          <td>
                            {submittedAt
                              ? formatHHmmDDMMYYYY(submittedAt)
                              : '--'}
                          </td>
                          <td className="text-center">
                            <CursorPointer
                              id={`attempt-eye-${idx}`}
                              onClick={() =>
                                setQuestionDetailModal({
                                  attempt,
                                  user: selectedHistory.history.user,
                                })
                              }
                            >
                              <Eye size={15} />
                              <UncontrolledTooltip
                                placement="top"
                                target={`attempt-eye-${idx}`}
                              >
                                Question detail
                              </UncontrolledTooltip>
                            </CursorPointer>
                          </td>
                          <td>{selectedHistory.history.user?.fullName}</td>
                          <HistoryStatusCell
                            $color={statusColor}
                            style={{ color: statusColor }}
                          >
                            {statusText}
                          </HistoryStatusCell>
                        </tr>
                      );
                    })}
                    {!selectedHistory.history.attempts?.length && (
                      <tr>
                        <td colSpan={4} className="text-center">
                          No history found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </ModalBody>
          </Modal>
        )}

        {questionDetailModal && (
          <Modal
            isOpen
            toggle={() => setQuestionDetailModal(null)}
            size="lg"
            centered
            zIndex={2060}
            backdrop="static"
          >
            <ModalHeader toggle={() => setQuestionDetailModal(null)}>
              Question detail
            </ModalHeader>
            <ScrollableModalBody>
              <AdaptiveAttemptQuestionDetail
                attempt={questionDetailModal.attempt}
                user={questionDetailModal.user}
              />
            </ScrollableModalBody>
          </Modal>
        )}

        {unitSelected?.microLearningContent && (
          <SectionBlock>
            <StyledCollapse
              bordered={false}
              defaultActiveKey={[]}
              expandIconPosition="right"
            >
              <BorderlessPanel
                key="micro-learning"
                header={
                  <MicroHeaderRow>
                    <MicroIconBadge>
                      <ThunderboltOutlined />
                    </MicroIconBadge>
                    <MicroTitle>Micro Learning</MicroTitle>
                    <AiPill>AI</AiPill>
                  </MicroHeaderRow>
                }
              >
                <MicroBody>
                  {stripHtml(
                    String(unitSelected.microLearningContent || ''),
                  ).trim()}
                </MicroBody>
              </BorderlessPanel>
            </StyledCollapse>
          </SectionBlock>
        )}

        {unitSelected?.result?.length ? (
          <div>
            <BreakdownHeading>Question Breakdown</BreakdownHeading>
            <QuestionCollapse
              bordered={false}
              defaultActiveKey={[]}
              expandIconPosition="right"
            >
              {unitSelected?.result?.map((item, index) => {
                const previewText = stripHtml(
                  String(item?.content || item?.contentHTML || ''),
                ).trim();
                const truncated =
                  previewText.length > 120
                    ? `${previewText.slice(0, 120)}…`
                    : previewText || `Question ${index + 1}`;
                const weightText = getQuestionWeightLabel(item);
                const weightTone = getWeightTone(item?.status);

                return (
                  <QuestionPanel
                    key={item._id ?? index}
                    header={
                      <QHeaderRow>
                        <QuestionStatusBadge status={item?.status} />
                        <QNumberLabel>Q{index + 1}</QNumberLabel>
                        <QPreviewText title={previewText}>
                          {truncated}
                        </QPreviewText>
                        {weightText != null && (
                          <WeightPill title="Weightage">
                            <span>{weightText}</span>
                          </WeightPill>
                        )}
                      </QHeaderRow>
                    }
                  >
                    <QuestionBody>
                      {renderQuestionStem(item, index)}
                    </QuestionBody>
                    <QuestionAnswerSection>
                      <AdaptiveAnswerReadOnly item={item} />
                    </QuestionAnswerSection>
                  </QuestionPanel>
                );
              })}
            </QuestionCollapse>
          </div>
        ) : (
          <EmptyStateCard>No result found</EmptyStateCard>
        )}
      </Container>
    </PageShell>
  );
};

export default TrackingResultAdaptive;
